import type {
    FC,
    KeyboardEvent,
    PointerEvent,
    ReactNode
} from 'react'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useElementWidth } from '@/hooks/use-element-width'
import { formatTick, formatValue, niceScale } from '@/components/charts/chart-utils'

export interface LineSeries {
    name: string
    values: Array<number | null>
}

interface LineChartProps {
    title: string
    labels: Array<string>
    series: Array<LineSeries>
    unit?: string
    target?: {
        value: number
        label: string
    }
    targetDashed?: boolean
    height?: number
    emptyMessage?: string
    className?: string
}

const MARGIN = { top: 16, right: 58, bottom: 30, left: 46 }
const SERIES_COLORS = ['#ECE6D8', '#8A8478']
const GRID = '#26231F'
const BASELINE = '#3A362F'
const SIGNAL = '#86B6F0'
const MUTE = '#8A8478'
const INK = '#ECE6D8'
const SURFACE = '#0A0908'

const lastIndexOf = (values: Array<number | null>): number => {
    for (let i = values.length - 1; i >= 0; i--) if (values[i] !== null) return i

    return -1
}

/**
 * Thin-line chart with hairline grid, an optional blue target line, a hover and keyboard crosshair,
 * and a visually hidden table carrying every value.
 */
export const LineChart: FC<LineChartProps> = ({
    title,
    labels,
    series,
    unit,
    target,
    targetDashed = false,
    height = 260,
    emptyMessage = 'No readings yet',
    className
}): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const width = useElementWidth(containerRef, 720)
    const [active, setActive] = useState<number | null>(null)

    const values = series.flatMap(item => item.values.filter((value): value is number => value !== null))
    const hasData = values.length > 0
    const scale = niceScale([...values, ...(target ? [target.value] : [])], 5)
    const plotWidth = Math.max(40, width - MARGIN.left - MARGIN.right)
    const plotHeight = height - MARGIN.top - MARGIN.bottom
    const count = labels.length
    const span = scale.max - scale.min || 1

    const x = (index: number): number => MARGIN.left + (count <= 1 ? plotWidth / 2 : (index / (count - 1)) * plotWidth)
    const y = (value: number): number => MARGIN.top + plotHeight - ((value - scale.min) / span) * plotHeight

    const paths = series.map(item => {
        let d = ''
        let drawing = false

        item.values.forEach((value, index) => {
            if (value === null) {
                drawing = false

                return
            }

            d += `${drawing ? 'L' : 'M'}${x(index).toFixed(1)},${y(value).toFixed(1)}`
            drawing = true
        })

        return d
    })

    const maxLabels = Math.max(2, Math.floor(plotWidth / 70))
    const every = Math.max(1, Math.ceil(count / maxLabels))
    const ends = series.map(item => lastIndexOf(item.values))
    const endY = series.map((item, index) => (ends[index] >= 0 ? y(item.values[ends[index]] as number) : null))
    const showSecondEnd = series.length > 1 && endY[0] !== null && endY[1] !== null && Math.abs((endY[0] as number) - (endY[1] as number)) > 14

    const updateFromPointer = (event: PointerEvent<SVGRectElement>): void => {
        const rect = event.currentTarget.getBoundingClientRect()
        const ratio = (event.clientX - rect.left) / Math.max(1, rect.width)
        const index = Math.round(ratio * (count - 1))

        setActive(Math.min(count - 1, Math.max(0, index)))
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (!hasData) return

        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault()
            event.stopPropagation()

            const step = event.key === 'ArrowRight' ? 1 : -1

            setActive(current => Math.min(count - 1, Math.max(0, (current ?? (step > 0 ? -1 : count)) + step)))
        }
        else if (event.key === 'Home') {
            event.preventDefault()
            setActive(0)
        }
        else if (event.key === 'End') {
            event.preventDefault()
            setActive(count - 1)
        }
        else if (event.key === 'Escape') {
            setActive(null)
        }
    }

    const tooltipLeft = active === null ? 0 : x(active)
    const flip = tooltipLeft > width - 190

    return (
        <div className={cn('w-full', className)}>
            {series.length > 1 || target ? (
                <div className='mb-4 flex flex-wrap items-center gap-x-6 gap-y-2' aria-hidden='true'>
                    {series.map((item, index) => (
                        <span key={item.name} className='hud flex items-center gap-2 text-mute'>
                            <span className='inline-block h-[2px] w-4' style={{ backgroundColor: SERIES_COLORS[index] ?? MUTE }} />
                            {item.name}
                        </span>
                    ))}

                    {target && (
                        <span className='hud flex items-center gap-2 text-mute'>
                            <span className={cn('inline-block w-4', targetDashed ? 'border-t border-dashed border-signal' : 'h-px bg-signal')} />
                            {target.label}
                        </span>
                    )}
                </div>
            ) : null}

            <div
                ref={containerRef}
                tabIndex={hasData ? 0 : -1}
                role='group'
                aria-label={`${title}. Use the left and right arrow keys to read each value.`}
                onKeyDown={onKeyDown}
                onBlur={() => setActive(null)}
                className='relative w-full select-none'
                style={{ height }}
            >
                <svg width={width} height={height} className='block overflow-visible' aria-hidden='true'>
                    {scale.ticks.map(tick => (
                        <g key={tick}>
                            <line
                                x1={MARGIN.left}
                                x2={MARGIN.left + plotWidth}
                                y1={y(tick)}
                                y2={y(tick)}
                                stroke={tick === scale.min ? BASELINE : GRID}
                                strokeWidth={1}
                                shapeRendering='crispEdges'
                            />

                            <text x={MARGIN.left - 10} y={y(tick)} dy='0.32em' textAnchor='end' fill={MUTE} className='font-mono text-[10px] tracking-[0.08em]'>
                                {formatTick(tick)}
                            </text>
                        </g>
                    ))}

                    {labels.map((label, index) => (index % every === 0 || index === count - 1) && (index === count - 1 || count - 1 - index >= every / 2) ? (
                        <text
                            key={`${label}-${index}`}
                            x={x(index)}
                            y={height - 8}
                            textAnchor='middle'
                            fill={MUTE}
                            className='font-mono text-[10px] uppercase tracking-[0.1em]'
                        >
                            {label}
                        </text>
                    ) : null)}

                    {target && (
                        <line
                            x1={MARGIN.left}
                            x2={MARGIN.left + plotWidth}
                            y1={y(target.value)}
                            y2={y(target.value)}
                            stroke={SIGNAL}
                            strokeWidth={1}
                            strokeDasharray={targetDashed ? '6 5' : undefined}
                            shapeRendering='crispEdges'
                        />
                    )}

                    {paths.map((d, index) => (
                        <path
                            key={series[index].name}
                            d={d}
                            fill='none'
                            stroke={SERIES_COLORS[index] ?? MUTE}
                            strokeWidth={1.5}
                            strokeLinejoin='round'
                            strokeLinecap='round'
                        />
                    ))}

                    {series.map((item, index) => {
                        const end = ends[index]
                        const cy = endY[index]

                        if (end < 0 || cy === null || (index === 1 && !showSecondEnd) || index > 1) return null

                        return (
                            <g key={`end-${item.name}`}>
                                <circle cx={x(end)} cy={cy} r={4} fill={SERIES_COLORS[index]} stroke={SURFACE} strokeWidth={2} />

                                <text x={x(end) + 10} y={cy} dy='0.32em' fill={index === 0 ? INK : MUTE} className='font-mono text-[11px] tracking-[0.06em]'>
                                    {formatTick(item.values[end] as number)}
                                </text>
                            </g>
                        )
                    })}

                    {active !== null && hasData && (
                        <g>
                            <line x1={x(active)} x2={x(active)} y1={MARGIN.top} y2={MARGIN.top + plotHeight} stroke='rgba(236,230,216,0.35)' strokeWidth={1} shapeRendering='crispEdges' />

                            {series.map((item, index) => {
                                const value = item.values[active]

                                if (value === null || value === undefined) return null

                                return <circle key={`active-${item.name}`} cx={x(active)} cy={y(value)} r={4} fill={SERIES_COLORS[index] ?? MUTE} stroke={SURFACE} strokeWidth={2} />
                            })}
                        </g>
                    )}

                    {hasData && (
                        <rect
                            x={MARGIN.left}
                            y={MARGIN.top}
                            width={plotWidth}
                            height={plotHeight}
                            fill='transparent'
                            onPointerMove={updateFromPointer}
                            onPointerDown={updateFromPointer}
                            onPointerLeave={() => setActive(null)}
                        />
                    )}
                </svg>

                {active !== null && hasData && (
                    <div
                        className='pointer-events-none absolute top-0 z-10 min-w-[160px] border border-hairline bg-panel px-3 py-2'
                        style={{
                            left: tooltipLeft,
                            transform: flip ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)'
                        }}
                    >
                        <p className='hud text-mute'>
                            {labels[active]}
                        </p>

                        {series.map((item, index) => (
                            <p key={item.name} className='hud flex items-center justify-between gap-4 text-ink'>
                                <span className='flex items-center gap-2'>
                                    <span className='inline-block h-[2px] w-3' style={{ backgroundColor: SERIES_COLORS[index] ?? MUTE }} />
                                    {series.length > 1 ? item.name : 'Value'}
                                </span>

                                <span>
                                    {formatValue(item.values[active] ?? null, unit)}
                                </span>
                            </p>
                        ))}

                        {target && (
                            <p className='hud flex items-center justify-between gap-4 text-mute'>
                                <span className='flex items-center gap-2'>
                                    <span className={cn('inline-block w-3', targetDashed ? 'border-t border-dashed border-signal' : 'h-px bg-signal')} />
                                    Target
                                </span>

                                <span>
                                    {formatValue(target.value, unit)}
                                </span>
                            </p>
                        )}
                    </div>
                )}

                {!hasData && (
                    <div className='absolute inset-0 grid place-items-center px-6'>
                        <p className='hud max-w-[42ch] border border-hairline bg-background px-4 py-3 text-center text-mute'>
                            {emptyMessage}
                        </p>
                    </div>
                )}
            </div>

            <table className='sr-only'>
                <caption>
                    {title}
                </caption>

                <thead>
                    <tr>
                        <th scope='col'>
                            Period
                        </th>

                        {series.map(item => (
                            <th key={item.name} scope='col'>
                                {item.name}
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {labels.map((label, index) => (
                        <tr key={`${label}-${index}`}>
                            <th scope='row'>
                                {label}
                            </th>

                            {series.map(item => (
                                <td key={item.name}>
                                    {formatValue(item.values[index] ?? null, unit)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
